import { Box, useTheme } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { MemoEmptyChart } from 'charts/common/EmptyChart';
import type { BuildChartContext, ChartData } from '../catalog/types';
import { CHART_AXIS_MARGIN } from '../tokens/muiChartLayout';
import type { ReactElement } from 'react';
import { truncateAxisLabel } from '../formatters/truncateAxisLabel';
import {
  CHART_AXIS_LABEL_MAX_LENGTH,
  getChartAreaSx,
  getChartAxisSlotProps,
  getChartGridColor,
  getChartLegendSlotProps,
} from '../theme/muiChartSlotProps';

type LineChartViewProps = Readonly<{
  data: ChartData;
  height: number;
  colors: readonly string[];
  variant: BuildChartContext['variant'];
}>;

export function LineChartView({
  data,
  height,
  colors,
  variant,
}: LineChartViewProps): ReactElement {
  const theme = useTheme();
  const lineSeries = data.lineSeries ?? [];
  const categories = data.categories ?? data.labels;

  if (lineSeries.length === 0 || categories.length === 0) {
    return <MemoEmptyChart variant={variant} />;
  }

  const maxLabelLength = CHART_AXIS_LABEL_MAX_LENGTH[variant];
  const axisCategories = categories.map((label) =>
    variant === 'dashboard' ? truncateAxisLabel(label, maxLabelLength) : label
  );

  const chart = (
    <LineChart
        xAxis={[
          {
            scaleType: 'point',
            data: axisCategories,
            tickLabelStyle: getChartAxisSlotProps(variant).tickLabelStyle,
          },
        ]}
        yAxis={[
          {
            tickLabelStyle: getChartAxisSlotProps(variant).tickLabelStyle,
          },
        ]}
        grid={{ horizontal: true }}
        sx={{
          '& .MuiChartsGrid-line': {
            stroke: getChartGridColor(theme),
            strokeOpacity: 0.4,
          },
          '& .MuiAreaElement-root': {
            fillOpacity: 0.15,
          },
        }}
        series={lineSeries.map((entry, index) => {
          const baseColor = colors[index % colors.length] ?? colors[0];
          return {
            data: [...entry.values],
            label: entry.label,
            showMark: true,
            curve: 'monotoneX' as const,
            area: true,
            color: baseColor,
            valueFormatter: (value: number | null) =>
              value === null ? '' : String(Math.floor(value)),
          };
        })}
        height={height}
        margin={CHART_AXIS_MARGIN}
        slotProps={getChartLegendSlotProps()}
      />
  );

  if (variant === 'dashboard') {
    return (
      <Box
        sx={{
          width: '100%',
          flex: '0 0 auto',
          minWidth: 0,
          height,
          overflow: 'hidden',
        }}
      >
        {chart}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...getChartAreaSx(),
        height,
        minHeight: height,
        width: '100%',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          maxHeight: '100%',
        }}
      >
        {chart}
      </Box>
    </Box>
  );
}
